import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  redirect,
  Outlet,
  type SearchSchemaInput,
} from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import { getStoredUser, isAuthenticated } from '@/auth/use-auth';
import { isKeycloakMode } from '@/config/env';
import { LayoutRoot, layoutForRouting } from '@/shared/layout-root';
import { classicHomeRedirect } from '@/shared/nav-permissions';
import { profileQueryOptions } from '@/features/account/account-queries';
import { layoutRedirect } from '@/shared/simple-nav';
import { HojePage, hojeSearchSchema, type HojeSearch } from '@/features/work/HojePage';
import { AtendimentosPage } from '@/features/atendimentos/AtendimentosPage';
import {
  AtendimentoPage,
  atendimentoSearchSchema,
  type AtendimentoSearch,
} from '@/features/atendimentos/AtendimentoPage';
import {
  ConversasPage,
  conversasSearchSchema,
  type ConversasSearch,
} from '@/features/conversas/ConversasPage';
import {
  atendimentosSearchSchema,
  type AtendimentosSearch,
} from '@/features/atendimentos/atendimentos-search';
import { LoginPage } from '@/features/auth/LoginPage';
import { DashboardPage } from '@/features/dashboard/DashboardPage';
import { TicketsPage, ticketsSearchSchema } from '@/features/tickets/TicketsPage';
import { TicketDetailPage } from '@/features/tickets/TicketDetailPage';
import { TicketSearchPage, ticketSearchSearchSchema } from '@/features/tickets/TicketSearchPage';
import {
  DispatchesPage,
  dispatchesSearchSchema,
  type DispatchesSearch,
} from '@/features/dispatches/DispatchesPage';
import { ChatsPage, chatsSearchSchema } from '@/features/chats/ChatsPage';
import { PublicChatPage } from '@/features/chats/PublicChatPage';
import { ClientsPage, clientsSearchSchema } from '@/features/clients/ClientsPage';
import { ClientDetailPage } from '@/features/clients/ClientDetailPage';
import { BillingPage, billingSearchSchema } from '@/features/billing/BillingPage';
import { DisplacementsSettingsPage } from '@/features/billing/displacements-settings-page';
import { LooseServicesSettingsPage } from '@/features/billing/loose-services-settings-page';
import { ExtraValuesSettingsPage } from '@/features/billing/extra-values-settings-page';
import { ResourcesPage } from '@/features/resources/ResourcesPage';
import { VehiclePage } from '@/features/sgr/VehiclePage';
import { EndCustomerPage } from '@/features/sgr/EndCustomerPage';
import { DesksPage } from '@/features/desks/DesksPage';
import { DeskSettingsPage } from '@/features/desks/DeskSettingsPage';
import { AutomationsSettingsPage } from '@/features/desks/AutomationsSettingsPage';
import { ScheduledSettingsPage } from '@/features/desks/ScheduledSettingsPage';
import { ChecklistsSettingsPage } from '@/features/desks/ChecklistsSettingsPage';
import { HolidaysSettingsPage } from '@/features/desks/HolidaysSettingsPage';
import {
  CatalogsSettingsPage,
  GroupsSettingsPage,
  ModelsSettingsPage,
} from '@/features/settings/settings-section-pages';
import { AppointmentsPage, appointmentsSearchSchema } from '@/features/appointments/AppointmentsPage';
import { AgendaPage, agendaSearchSchema } from '@/features/agenda/AgendaPage';
import { AccountPage } from '@/features/account/AccountPage';
import { KnowledgeFoldersPage } from '@/features/knowledge/KnowledgeFoldersPage';
import { KnowledgeFolderPage } from '@/features/knowledge/KnowledgeFolderPage';
import { KnowledgeArticlePage } from '@/features/knowledge/KnowledgeArticlePage';
import {
  KnowledgeEditPage,
  KnowledgeNewPage,
  knowledgeNewSearchSchema,
} from '@/features/knowledge/KnowledgeEditorPage';
import { PublicKbArticlePage, PublicKbListPage } from '@/features/knowledge/PublicKbPage';
import { VaultPage } from '@/features/vault/VaultPage';
import { SettingsPage } from '@/features/settings/SettingsPage';
import { IntegrationsPage } from '@/features/settings/IntegrationsPage';
import { OrganizationSettingsPage } from '@/features/settings/OrganizationSettingsPage';
import { ChatSettingsPage } from '@/features/settings/ChatSettingsPage';
import { QuickRepliesPage } from '@/features/settings/QuickRepliesPage';
import { TicketGroupsPage } from '@/features/settings/TicketGroupsPage';
import { TechniciansPage } from '@/features/service-orders/TechniciansPage';
import { PausesPage } from '@/features/presence/PausesPage';
import { ServiceOrderPrintPage } from '@/features/service-orders/ServiceOrderPrintPage';
import { ImportsPage } from '@/features/settings/ImportsPage';
import { AiSettingsPage } from '@/features/settings/AiSettingsPage';
import { CampaignsPage } from '@/features/campaigns/CampaignsPage';
import { UsersPage, usersSearchSchema } from '@/features/settings/UsersPage';
import { ImpersonationSessionsPage } from '@/features/impersonation/ImpersonationSessionsPage';
import { EntitiesPage } from '../features/entities/EntitiesPage';
import { PermissionGroupsPage } from '@/features/permissions/PermissionGroupsPage';
import { PermissionGroupDetailPage } from '@/features/permissions/PermissionGroupDetailPage';
import { ReportsPage, reportsSearchSchema, type ReportsSearch } from '../features/reports/ReportsPage';
import { RatePage } from '../features/portal/RatePage';
import { FormPage } from '../features/portal/FormPage';
import { PortalLayout } from '../features/portal/PortalLayout';
import { PortalDashboard } from '../features/portal/PortalDashboard';
import { PortalTickets } from '../features/portal/PortalTickets';
import { PortalTicketDetail } from '../features/portal/PortalTicketDetail';
import { PortalNewTicket } from '../features/portal/PortalNewTicket';
import { isClientRole } from '../features/portal/portal-auth';
import { PortalNewsPage, StaffNewsPage } from '@/features/novidades/NewsPage';
import {
  NotificationsPage,
  notificationsSearchSchema,
  type NotificationsSearch,
} from '@/features/notifications/NotificationsPage';
import { accountSearchSchema, type AccountSearch } from '@/features/account/account-search';
import { queryClient } from '@/lib/query-client';

export interface RouterContext {
  query: QueryClient;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => <Outlet />,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  beforeLoad: () => {
    // Modo keycloak: não há tela de login local — quem autentica é a página
    // (temada) do Keycloak, disparada pelo AuthGate.
    if (isKeycloakMode) throw redirect({ to: '/' });
  },
  component: LoginPage,
});

const authenticatedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_authenticated',
  beforeLoad: async ({ context, location }) => {
    if (isAuthenticated()) {
      // Role client não usa o app staff: pós-login (LoginPage navega para /)
      // e qualquer deep-link interno caem aqui e vão para o portal.
      const user = getStoredUser();
      if (user && isClientRole(user.role)) throw redirect({ to: '/portal' });
    } else if (!isKeycloakMode) {
      // Modo keycloak: o AuthGate só renderiza o router autenticado; se faltar
      // sessão, é ele quem chama keycloak.login() — nunca o /login local.
      throw redirect({ to: '/login' });
    }
    // spec 018: a MESMA intenção no layout da pessoa — o link do Completo
    // abre o Simplificado e vice-versa, antes de montar a tela errada
    const layout = await layoutForRouting(context.query);
    const target = layoutRedirect(layout, location.pathname, location.search as Record<string, unknown>);
    if (target) throw redirect({ ...target, replace: true } as never);
  },
  // spec 017 D3 + 018: Completo (congelado) ou Simplificado, pela preferência
  component: LayoutRoot,
});

const dashboardRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/',
  staticData: { title: 'Dashboard' },
  // spec 058: o Dashboard é de tickets — quem só atende conversas entra nelas
  beforeLoad: async ({ context }) => {
    const profile = await context.query.ensureQueryData(profileQueryOptions()).catch(() => null);
    const target = classicHomeRedirect(profile?.permissions);
    if (target) throw redirect({ to: target, replace: true } as never);
  },
  component: DashboardPage,
});

// ── Layout Simplificado (spec 018) ──────────────────────────────────────────
// `/s/*` só existe no Simplificado: no Completo o beforeLoad acima leva para o
// equivalente (layoutRedirect). As demais telas são as de sempre, montadas no
// shell simplificado.
const simpleHojeRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/s/hoje',
  validateSearch: (raw: Partial<HojeSearch> & SearchSchemaInput) => hojeSearchSchema.parse(raw),
  staticData: { title: 'Hoje' },
  component: HojePage,
});

const simpleAtendimentosRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/s/atendimentos',
  validateSearch: (raw: Partial<AtendimentosSearch> & SearchSchemaInput) =>
    atendimentosSearchSchema.parse(raw),
  staticData: { title: 'Atendimentos' },
  component: AtendimentosPage,
});

const simpleAtendimentoRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/s/atendimentos/$ticketNumber',
  validateSearch: (raw: Partial<AtendimentoSearch> & SearchSchemaInput) => atendimentoSearchSchema.parse(raw),
  staticData: { title: (params) => `Atendimento #${params.ticketNumber ?? ''}` },
  component: AtendimentoPage,
});

const simpleConversasRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/s/conversas',
  validateSearch: (raw: Partial<ConversasSearch> & SearchSchemaInput) => conversasSearchSchema.parse(raw),
  staticData: { title: 'Conversas' },
  component: ConversasPage,
});

// o painel do Completo (horas, feed, contagem por mesa) — aba de Gestão
const simplePainelRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/s/painel',
  staticData: { title: 'Painel' },
  component: DashboardPage,
});

const ticketsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/tickets',
  validateSearch: (raw) => ticketsSearchSchema.parse(raw),
  staticData: { title: 'Tickets' },
  component: TicketsPage,
});

// Busca por nome/placa/telefone em todas as mesas (spec 012). Segmento
// estático: o router o prefere a `/tickets/$ticketNumber`.
const ticketSearchRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/tickets/search',
  validateSearch: (raw) => ticketSearchSearchSchema.parse(raw),
  staticData: { title: 'Buscar tickets' },
  component: TicketSearchPage,
});

// "Disparos" (spec 025): os modelos de WhatsApp da regra de disparo — quando
// saíram, se chegaram, se responderam. Estática, como /tickets/search.
const ticketDispatchesRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/tickets/dispatches',
  validateSearch: (raw: Partial<DispatchesSearch> & SearchSchemaInput) => dispatchesSearchSchema.parse(raw),
  staticData: { title: 'Disparos' },
  component: DispatchesPage,
});

const ticketDetailRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/tickets/$ticketNumber',
  staticData: { title: (params) => `Ticket #${params.ticketNumber ?? ''}` },
  component: TicketDetailPage,
});

const chatsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/chats',
  validateSearch: (raw) => chatsSearchSchema.parse(raw),
  staticData: { title: 'Chats' },
  component: ChatsPage,
});

const clientsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/clients',
  validateSearch: (raw) => clientsSearchSchema.parse(raw),
  staticData: { title: 'Clientes' },
  component: ClientsPage,
});

const clientDetailRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/clients/$clientId',
  staticData: { title: 'Clientes' },
  component: ClientDetailPage,
});

const resourcesRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/resources',
  staticData: { title: 'Recursos' },
  component: ResourcesPage,
});

// spec 032: a página do recurso (o veículo do SGR, com os tickets dele) e a do
// associado — toda a equipe; o portal nunca (o _authenticated manda o role
// client para /portal, e a API barra com requireStaff)
const resourceDetailRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/resources/$resourceId',
  staticData: { title: 'Recursos' },
  component: VehiclePage,
});

const endCustomerRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/end-customers/$endCustomerId',
  staticData: { title: 'Recursos' },
  component: EndCustomerPage,
});

const contractsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/contracts',
  validateSearch: (raw) => billingSearchSchema.parse(raw),
  staticData: { title: 'Contratos' },
  component: BillingPage,
});

// v8: as telas de Mesa de serviço moraram em /desks, /catalogs, /models e
// /groups; agora vivem sob /settings (padrão TiFlux) — as rotas antigas viram
// redirects para não quebrar links salvos.
const desksRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/desks',
  beforeLoad: () => {
    throw redirect({ to: '/settings/desks' });
  },
});

const deskSettingsLegacyRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/desks/$deskId',
  beforeLoad: ({ params }) => {
    throw redirect({ to: '/settings/desks/$deskId', params: { deskId: params.deskId } });
  },
});

const catalogsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/catalogs',
  beforeLoad: () => {
    throw redirect({ to: '/settings/desks/catalogs' });
  },
});

// v5: /templates foi consolidada na aba "E-mail" de /models (hoje em
// /settings/desks/models) — redirect.
const templatesRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/templates',
  beforeLoad: () => {
    throw redirect({ to: '/settings/desks/models' });
  },
});

const modelsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/models',
  beforeLoad: () => {
    throw redirect({ to: '/settings/desks/models' });
  },
});

// v12: a triagem de pré-tickets saiu da sidebar e virou a view
// `?view=pretickets` de /tickets — a rota antiga vira redirect (links e
// favoritos continuam válidos).
const preticketsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/pretickets',
  beforeLoad: () => {
    throw redirect({ to: '/tickets', search: { view: 'pretickets', status: 'open', q: '' } });
  },
});

const appointmentsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/appointments',
  validateSearch: (raw) => appointmentsSearchSchema.parse(raw),
  staticData: { title: 'Apontamentos' },
  component: AppointmentsPage,
});

const agendaRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/agenda',
  validateSearch: (raw) => agendaSearchSchema.parse(raw),
  staticData: { title: 'Agenda' },
  component: AgendaPage,
});

const accountRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/account',
  // spec 037: a aba na URL — o "Configurações" do sino abre direto em Notificações
  validateSearch: (raw: Partial<AccountSearch> & SearchSchemaInput) => accountSearchSchema.parse(raw),
  staticData: { title: 'Minha conta' },
  component: AccountPage,
});

// Notificações (spec 037): o histórico do sino — o "Ver notificações" leva para cá
const notificationsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/notificacoes',
  validateSearch: (raw: Partial<NotificationsSearch> & SearchSchemaInput) =>
    notificationsSearchSchema.parse(raw),
  staticData: { title: 'Notificações' },
  component: NotificationsPage,
});

// Novidades (spec 036): o histórico do que mudou, por mês — o painel do
// megafone leva para cá ("Ver todas")
const newsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/novidades',
  staticData: { title: 'Novidades' },
  component: StaffNewsPage,
});

// ── Base de conhecimento (interna) ──────────────────────────────────────────
const knowledgeRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/knowledge',
  staticData: { title: 'Conhecimentos' },
  component: KnowledgeFoldersPage,
});

const knowledgeNewRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/knowledge/new',
  validateSearch: (raw) => knowledgeNewSearchSchema.parse(raw),
  staticData: { title: 'Conhecimentos' },
  component: KnowledgeNewPage,
});

const knowledgeFolderRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/knowledge/folder/$folderId',
  staticData: { title: 'Conhecimentos' },
  component: KnowledgeFolderPage,
});

const knowledgeArticleRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/knowledge/$knowledgeId',
  staticData: { title: 'Conhecimentos' },
  component: KnowledgeArticlePage,
});

const knowledgeEditRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/knowledge/$knowledgeId/edit',
  staticData: { title: 'Conhecimentos' },
  component: KnowledgeEditPage,
});

// ── Cofre de senhas ─────────────────────────────────────────────────────────
const vaultRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/vault',
  staticData: { title: 'Cofre de senhas' },
  component: VaultPage,
});

// ── Grupos de atendentes (v4; v8: mudou para /settings/groups) ──────────────
const groupsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/groups',
  beforeLoad: () => {
    throw redirect({ to: '/settings/groups' });
  },
});

// ── Configurações (v7): hub no padrão TiFlux. v8: as telas de Mesa de
// serviço (mesas, automações, atividades, checklists, feriados, catálogos,
// modelos) e Grupos de atendentes viraram filhas de /settings ──────────────
const settingsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/settings',
  staticData: { title: 'Configurações' },
  component: SettingsPage,
});

const settingsDesksRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/settings/desks',
  staticData: { title: 'Mesas de serviço' },
  component: DesksPage,
});

// Estáticas antes da dinâmica: automations/scheduled/… não podem casar $deskId
// (o ranking do TanStack Router já garante, mas a ordem documenta a intenção).
const settingsDesksAutomationsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/settings/desks/automations',
  staticData: { title: 'Automações' },
  component: AutomationsSettingsPage,
});

const settingsDesksScheduledRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/settings/desks/scheduled',
  staticData: { title: 'Atividades agendadas' },
  component: ScheduledSettingsPage,
});

const settingsDesksChecklistsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/settings/desks/checklists',
  staticData: { title: 'Checklists' },
  component: ChecklistsSettingsPage,
});

const settingsDesksHolidaysRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/settings/desks/holidays',
  staticData: { title: 'Feriados' },
  component: HolidaysSettingsPage,
});

const settingsDesksCatalogsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/settings/desks/catalogs',
  staticData: { title: 'Catálogos' },
  component: CatalogsSettingsPage,
});

const settingsDesksModelsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/settings/desks/models',
  staticData: { title: 'Modelos' },
  component: ModelsSettingsPage,
});

const settingsDeskDetailRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/settings/desks/$deskId',
  staticData: { title: 'Mesas de serviço' },
  component: DeskSettingsPage,
});

const settingsGroupsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/settings/groups',
  staticData: { title: 'Grupos de atendentes' },
  component: GroupsSettingsPage,
});

// Cadastros financeiros (v9 — DESIGN-V9 Etapa 1): deslocamentos, serviços
// avulsos e valores extras. Leitura livre p/ staff (necessária p/ lançar nos
// tickets); escrita exige contracts.manage — a API barra, o front esconde.
const settingsDisplacementsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/settings/displacements',
  staticData: { title: 'Deslocamentos' },
  component: DisplacementsSettingsPage,
});

const settingsLooseServicesRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/settings/loose-services',
  staticData: { title: 'Serviços avulsos' },
  component: LooseServicesSettingsPage,
});

const settingsExtraValuesRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/settings/extra-values',
  staticData: { title: 'Valores extras' },
  component: ExtraValuesSettingsPage,
});

// Integrações (admin-only — a página se auto-restringe, como a antiga /whatsapp)
const settingsIntegrationsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/settings/integrations',
  staticData: { title: 'Integrações' },
  component: IntegrationsPage,
});

// v11 — itens do hub que deixaram de ser "Em breve": Organização, Chat,
// Grupo de tickets, Importações e IA. Org/Chat/Importações/IA se
// auto-restringem (admin-style, como Integrações); Grupo de tickets é
// leitura para staff com escrita gated por desks.manage dentro da página.
const settingsOrganizationRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/settings/organization',
  staticData: { title: 'Organização' },
  component: OrganizationSettingsPage,
});

const settingsChatRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/settings/chat',
  staticData: { title: 'Configuração do chat' },
  component: ChatSettingsPage,
});

// respostas rápidas (PoolTrack, 02/10): leitura de quem atende; escrita admin —
// a página esconde os controles e a API barra
const settingsQuickRepliesRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/settings/quick-replies',
  staticData: { title: 'Respostas rápidas' },
  component: QuickRepliesPage,
});

const settingsTicketGroupsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/settings/ticket-groups',
  staticData: { title: 'Grupo de tickets' },
  component: TicketGroupsPage,
});

// spec 016: técnicos (leitura staff; escrita admin — a página esconde controles)
const settingsTechniciansRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/settings/technicians',
  staticData: { title: 'Técnicos' },
  component: TechniciansPage,
});

// spec 015: status da equipe e motivos de pausa (leitura staff; escrita admin)
const settingsPausesRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/settings/pauses',
  staticData: { title: 'Pausas' },
  component: PausesPage,
});

/**
 * spec 016 FR-006: versão de impressão da OS. Filha da RAIZ, não do
 * authenticatedRoute, para sair sem o AppLayout (menu/cabeçalho iriam para o
 * papel) — por isso repete a mesma guarda de sessão do authenticatedRoute.
 */
const serviceOrderPrintRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/tickets/$ticketNumber/service-orders/$id/print',
  beforeLoad: () => {
    if (isAuthenticated()) {
      const user = getStoredUser();
      if (user && isClientRole(user.role)) throw redirect({ to: '/portal' });
      return;
    }
    if (isKeycloakMode) return;
    throw redirect({ to: '/login' });
  },
  component: ServiceOrderPrintPage,
});

const settingsImportsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/settings/imports',
  staticData: { title: 'Importações' },
  component: ImportsPage,
});

const settingsAiRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/settings/ai',
  staticData: { title: 'Inteligência artificial' },
  component: AiSettingsPage,
});

// Campanhas de WhatsApp (v10 Etapa 2 — a página se auto-restringe a
// integrations.manage, mesma regra da API)
const settingsCampaignsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/settings/campaigns',
  staticData: { title: 'Campanhas' },
  component: CampaignsPage,
});

// Usuários (admin-only — a página se auto-restringe, como Integrações)
const settingsEntitiesRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/settings/entities',
  component: EntitiesPage,
  staticData: { title: 'Campos personalizados' },
});

const settingsUsersRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/settings/users',
  validateSearch: (raw) => usersSearchSchema.parse(raw),
  staticData: { title: 'Usuários' },
  component: UsersPage,
});

// spec 020: sessões de depuração "ver como cliente" (users.impersonate — a
// página se auto-restringe, a API barra)
const settingsImpersonationRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/settings/impersonation',
  staticData: { title: 'Sessões de depuração' },
  component: ImpersonationSessionsPage,
});

// Grupos de permissões (v7 — DESIGN-V7 §1): lista + detalhe. A API exige
// permission_groups.manage (admin sempre passa) — o front só ESCONDE o item
// do hub quando falta a permissão; a página em si mostra o erro da API.
const settingsPermissionGroupsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/settings/permission-groups',
  staticData: { title: 'Grupos de permissões' },
  component: PermissionGroupsPage,
});

const settingsPermissionGroupDetailRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/settings/permission-groups/$groupId',
  staticData: { title: 'Grupos de permissões' },
  component: PermissionGroupDetailPage,
});

// v7: /whatsapp foi consolidada na aba Comunicação de /settings/integrations.
const whatsappRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/whatsapp',
  beforeLoad: () => {
    throw redirect({ to: '/settings/integrations' });
  },
});

// relatório, filtros aplicados e página do extrato na URL — links para
// /reports sem search caem no padrão (extrato, últimos 30 dias, página 1)
const reportsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/reports',
  validateSearch: (raw: Partial<ReportsSearch> & SearchSchemaInput) => reportsSearchSchema.parse(raw),
  staticData: { title: 'Relatórios' },
  component: ReportsPage,
});

// ── Portal do cliente (v4): layout próprio, fora do AppLayout. Autenticado,
// mas exclusivo do role client — staff que abrir /portal volta para /. ──────
const portalRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/portal',
  beforeLoad: () => {
    if (!isAuthenticated()) {
      // Modo keycloak: o AuthGate cuida do login antes do router renderizar.
      if (isKeycloakMode) return;
      throw redirect({ to: '/login' });
    }
    const user = getStoredUser();
    if (user && !isClientRole(user.role)) throw redirect({ to: '/' });
  },
  component: PortalLayout,
});

const portalDashboardRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: '/',
  component: PortalDashboard,
});

const portalTicketsRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: '/tickets',
  component: PortalTickets,
});

// Estática antes da dinâmica: /tickets/new não pode casar $ticketNumber.
const portalNewTicketRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: '/tickets/new',
  component: PortalNewTicket,
});

const portalTicketDetailRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: '/tickets/$ticketNumber',
  component: PortalTicketDetail,
});

// Novidades do portal (spec 036): as que são para o cliente (audience portal)
const portalNewsRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: '/novidades',
  component: PortalNewsPage,
});

// ── Base de conhecimento PÚBLICA: fora do AppLayout/auth (filhas do root;
// o AuthGate faz bypass do Keycloak quando o pathname começa com /kb) ───────
const kbRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/kb',
  component: PublicKbListPage,
});

const kbArticleRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/kb/$slug',
  component: PublicKbArticlePage,
});

// ── Widget de chat PÚBLICO: fora do AppLayout/auth (filha do root; o AuthGate
// faz bypass do Keycloak em /chat — mas NÃO em /chats, que é interna) ───────
const publicChatRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/chat',
  component: PublicChatPage,
});

// ── Avaliação CSAT PÚBLICA: fora do AppLayout/auth (filha do root; o AuthGate
// faz bypass do Keycloak em /rate — link chega por e-mail ao solicitante) ────
const rateRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/rate/$token',
  component: RatePage,
});

// ── Formulário externo PÚBLICO (v5): fora do AppLayout/auth (filha do root; o
// AuthGate faz bypass do Keycloak em /form, como /rate — link compartilhado
// pela mesa para abertura de tickets sem login) ─────────────────────────────
const formRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/form/$token',
  component: FormPage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  kbRoute,
  kbArticleRoute,
  publicChatRoute,
  rateRoute,
  formRoute,
  serviceOrderPrintRoute,
  portalRoute.addChildren([
    portalDashboardRoute,
    portalTicketsRoute,
    portalNewTicketRoute,
    portalTicketDetailRoute,
    portalNewsRoute,
  ]),
  authenticatedRoute.addChildren([
    dashboardRoute,
    simpleHojeRoute,
    simpleAtendimentosRoute,
    simpleAtendimentoRoute,
    simpleConversasRoute,
    simplePainelRoute,
    ticketsRoute,
    ticketSearchRoute,
    ticketDispatchesRoute,
    ticketDetailRoute,
    chatsRoute,
    clientsRoute,
    clientDetailRoute,
    resourcesRoute,
    resourceDetailRoute,
    endCustomerRoute,
    contractsRoute,
    desksRoute,
    deskSettingsLegacyRoute,
    catalogsRoute,
    templatesRoute,
    modelsRoute,
    preticketsRoute,
    appointmentsRoute,
    agendaRoute,
    accountRoute,
    newsRoute,
    notificationsRoute,
    knowledgeRoute,
    knowledgeNewRoute,
    knowledgeFolderRoute,
    knowledgeArticleRoute,
    knowledgeEditRoute,
    vaultRoute,
    groupsRoute,
    settingsRoute,
    settingsDesksRoute,
    settingsDesksAutomationsRoute,
    settingsDesksScheduledRoute,
    settingsDesksChecklistsRoute,
    settingsDesksHolidaysRoute,
    settingsDesksCatalogsRoute,
    settingsDesksModelsRoute,
    settingsDeskDetailRoute,
    settingsGroupsRoute,
    settingsDisplacementsRoute,
    settingsLooseServicesRoute,
    settingsExtraValuesRoute,
    settingsPermissionGroupsRoute,
    settingsPermissionGroupDetailRoute,
    settingsIntegrationsRoute,
    settingsOrganizationRoute,
    settingsChatRoute,
    settingsQuickRepliesRoute,
    settingsTicketGroupsRoute,
    settingsTechniciansRoute,
    settingsPausesRoute,
    settingsImportsRoute,
    settingsAiRoute,
    settingsCampaignsRoute,
    settingsUsersRoute,
    settingsImpersonationRoute,
    settingsEntitiesRoute,
    whatsappRoute,
    reportsRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  context: { query: queryClient },
  defaultPreload: 'intent',
  scrollRestoration: true,
  // spec 018: no shell simplificado quem rola é o <main>, não a janela — sem
  // isto, abrir um atendimento a partir de uma lista rolada herdava a rolagem
  // dela. Só vale para navegação nova: o voltar continua restaurando.
  scrollToTopSelectors: ['[data-scroll-restoration-id="simple-main"]'],
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
  /**
   * `staticData.title` — título da topbar por rota, lido pelo AppLayout no
   * match mais profundo que o definir. Função quando o título depende de
   * params (ex.: número do ticket).
   */
  interface StaticDataRouteOption {
    title?: string | ((params: Record<string, string>) => string);
  }
}
